import { Node, mergeAttributes, isCellSelection, findParentNodeClosestToPos } from '@tiptap/core'
import { DecorationSet, Decoration } from 'prosemirror-view'
import { store } from '@/store'
import { Plugin, TextSelection } from 'prosemirror-state'

import {
  tableEditing,
  columnResizing,
  goToNextCell,
  addColumnBefore,
  addColumnAfter,
  deleteColumn,
  addRowBefore,
  addRowAfter,
  deleteRow,
  deleteTable,
  mergeCells,
  splitCell,
  toggleHeaderColumn,
  toggleHeaderRow,
  toggleHeaderCell,
  setCellAttr,
  fixTables
} from 'prosemirror-tables'

import { createTable } from 'prosemirror-utils'

// import { createTable } from './utilities/createTable'
import { TableView } from './TableView'

const setAttr = (attrs, name, value) => {
  const result = {}
  for (const prop in attrs) result[prop] = attrs[prop]
  result[name] = value
  return result
}

export const Table = Node.create({
  name: 'table',

  defaultOptions: {
    HTMLAttributes: {},
    handleWidth: 5,
    cellMinWidth: 25,
    View: TableView,
    lastColumnResizable: true,
    allowTableNodeSelection: false
  },

  addAttributes () {
    return {
      class: {
        default: ''
      }
    }
  },

  content: 'tableRow+',
  tableRole: 'table',
  isolating: true,
  group: 'block',

  parseHTML () {
    return [{
      tag: 'table',
      getAttrs: dom => {
        return { class: dom.getAttribute('class') }
      }
    }]
  },

  renderHTML ({ HTMLAttributes }) {
    return ['table', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), ['tbody', 0]]
  },

  addCommands () {
    return {
      insertTable: (options = { rows: 3, cols: 3, withHeaderRow: true }) => ({ tr, dispatch, editor }) => {
        const node = createTable(editor.schema, options.rows, options.cols, options.withHeaderRow)

        if (dispatch) {
          const offset = tr.selection.anchor + 1

          tr.replaceSelectionWith(node)
            .scrollIntoView()
            .setSelection(TextSelection.near(tr.doc.resolve(offset)))
        }

        return true
      },
      addColumnBefore: () => ({ state, dispatch }) => addColumnBefore(state, dispatch),
      addColumnAfter: () => ({ state, dispatch }) => addColumnAfter(state, dispatch),
      deleteColumn: () => ({ state, dispatch }) => deleteColumn(state, dispatch),
      addRowBefore: () => ({ state, dispatch }) => addRowBefore(state, dispatch),
      addRowAfter: () => ({ state, dispatch }) => addRowAfter(state, dispatch),
      deleteRow: () => ({ state, dispatch }) => deleteRow(state, dispatch),
      deleteTable: () => ({ state, dispatch }) => deleteTable(state, dispatch),
      mergeCells: () => ({ state, dispatch }) => mergeCells(state, dispatch),
      splitCell: () => ({ state, dispatch }) => splitCell(state, dispatch),
      toggleHeaderColumn: () => ({ state, dispatch }) => toggleHeaderColumn(state, dispatch),
      toggleHeaderRow: () => ({ state, dispatch }) => toggleHeaderRow(state, dispatch),
      toggleHeaderCell: () => ({ state, dispatch }) => toggleHeaderCell(state, dispatch),
      mergeOrSplit: () => ({ state, dispatch }) => {
        if (mergeCells(state, dispatch)) {
          return true
        }

        return splitCell(state, dispatch)
      },
      setCellAttribute: (name, value) => ({ state, dispatch }) => {
        return setCellAttr(name, value)(state, dispatch)
      },
      goToNextCell: () => ({ state, dispatch }) => goToNextCell(1)(state, dispatch),
      goToPreviousCell: () => ({ state, dispatch }) => goToNextCell(-1)(state, dispatch),
      fixTables: () => ({ state, dispatch }) => {
        if (dispatch) {
          fixTables(state)
        }

        return true
      },
      toggleTableVisibility: (value) => ({ state, dispatch }) => {
        const $pos = state.selection.$anchor

        for (let d = $pos.depth; d > 0; d--) {
          const node = $pos.node(d)

          if (node.type.spec.tableRole === 'table') {
            if (dispatch) {
              if (value) {
                dispatch(state.tr.setNodeMarkup($pos.before(d), null, setAttr(node.attrs, 'class', 'visible-off')))
              } else {
                dispatch(state.tr.setNodeMarkup($pos.before(d), null, setAttr(node.attrs, 'class', '')))
              }
            }
            return true
          }
        }
        return false
      }
    }
  },

  addKeyboardShortcuts () {
    const deleteTableWhenAllCellsSelected = () => {
      const { selection } = this.editor.state

      if (!isCellSelection(selection)) {
        return false
      }

      let cellCount = 0
      const table = findParentNodeClosestToPos(selection.ranges[0].$from, node => {
        return node.type.name === 'table'
      })

      table.node.descendants(node => {
        if (node.type.name === 'table') {
          return false
        }

        if (['tableCell', 'tableHeader'].includes(node.type.name)) {
          cellCount += 1
        }
      })

      const allCellsSelected = cellCount === selection.ranges.length

      if (!allCellsSelected) {
        return false
      }

      this.editor.commands.deleteTable()

      return true
    }

    return {
      Tab: () => {
        if (this.editor.commands.goToNextCell()) return true
        if (!this.editor.can().addRowAfter()) return false

        return this.editor.chain().addRowAfter().goToNextCell().run()
      },
      'Shift-Tab': () => this.editor.commands.goToPreviousCell(),
      Backspace: deleteTableWhenAllCellsSelected,
      'Mod-Backspace': deleteTableWhenAllCellsSelected,
      Delete: deleteTableWhenAllCellsSelected,
      'Mod-Delete': deleteTableWhenAllCellsSelected
    }
  },

  addProseMirrorPlugins () {
    return [
      ...([columnResizing({
        handleWidth: this.options.handleWidth,
        cellMinWidth: this.options.cellMinWidth,
        View: this.options.View,
        lastColumnResizable: this.options.lastColumnResizable
      })]),

      tableEditing({ allowTableNodeSelection: this.options.allowTableNodeSelection }),

      new Plugin({
        props: {
          decorations: ({ doc, plugins, selection }) => {
            const { isEditable, isFocused } = this.editor
            const active = isEditable || !this.options.showOnlyWhenEditable
            const { anchor } = selection
            const decorations = []

            if (!active || !isFocused) {
              return false
            }

            doc.descendants((node, pos) => {
              const hasAnchor = anchor >= pos && anchor <= (pos + node.nodeSize)

              if (hasAnchor && !node.isText && node.type.spec.tableRole === 'table') {
                const decoration = Decoration.node(pos, pos + node.nodeSize, {
                  class: 'activeTable'
                })
                store.commit('setTableActive', Math.random())
                decorations.push(decoration)
              }

              return false
            })

            if (!anchor || selection.$anchor.node(1)?.type.spec.tableRole !== 'table') {
              store.commit('setTableActive', false)
            }

            return DecorationSet.create(doc, decorations)
          }
        }
      })
    ]
  }
})
